import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { tiendaDb } from '../../../lib/supabase';
import {
  getAttachmentSignedUrl,
  aprobarFactura,
  eliminarFactura,
  marcarNoFactura,
  reprocesarIngesta,
} from '../../../services/gmailIngestService';

const ACCENT = '#34D399';
const GOLD = '#D6EF4E';
const BLUE = '#60A5FA';
const RED = '#F87171';
const AMBER = '#FBBF24';
const ORANGE = '#FB923C';
const PURPLE = '#A78BFA';
const CYAN = '#22D3EE';

const fmt = (n, d = 2) =>
  typeof n === 'number' ? n?.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d }) : '';
const fmtEur = (n) => (typeof n === 'number' ? `${fmt(n)} €` : '—');
const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(d)?.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return d; }
};

const inputCls = 'w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none focus:ring-1';
const inputSt = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', '--tw-ring-color': CYAN };

// ── CONFIRM DIALOG ────────────────────────────────────────────────────────────
const ConfirmDialog = ({ title, message, details, confirmLabel, confirmColor, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.90)' }}>
    <div className="w-full max-w-sm rounded-2xl" style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.12)' }}>
      <div className="px-6 py-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${confirmColor || RED}15`, border: `1px solid ${confirmColor || RED}30` }}>
            <Icon name="AlertTriangle" size={16} style={{ color: confirmColor || RED }} />
          </div>
          <h3 className="font-oranienbaum font-bold text-white text-base">{title}</h3>
        </div>
        <p className="font-oranienbaum text-white/60 text-sm mb-3">{message}</p>
        {details && (
          <div className="p-3 rounded-xl mb-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            {details?.map((d, i) => (
              <p key={i} className="font-oranienbaum text-white/70 text-xs">{d}</p>
            ))}
          </div>
        )}
      </div>
      <div className="flex gap-2 px-6 pb-5">
        <button onClick={onCancel}
          className="flex-1 px-4 py-2 rounded-xl text-sm font-oranienbaum"
          style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.60)', border: '1px solid rgba(255,255,255,0.10)' }}>
          Cancelar
        </button>
        <button onClick={onConfirm}
          className="flex-1 px-4 py-2 rounded-xl text-sm font-oranienbaum font-semibold"
          style={{ background: `${confirmColor || RED}20`, color: confirmColor || RED, border: `1px solid ${confirmColor || RED}40` }}>
          {confirmLabel || 'Confirmar'}
        </button>
      </div>
    </div>
  </div>
);

// ── PROVEEDOR SELECTOR ────────────────────────────────────────────────────────
const ProveedorSelector = ({ currentProveedorId, currentProveedorNombre, ocrNombre, ocrCif, onSelect, onClose }) => {
  const [proveedores, setProveedores] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newNombre, setNewNombre] = useState(ocrNombre || '');
  const [newCif, setNewCif] = useState(ocrCif || '');
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    tiendaDb?.from('proveedores')?.select('id, nombre, nif_cif, email')?.eq('activo', true)?.order('nombre')
      ?.then(({ data }) => setProveedores(data || []))
      ?.finally(() => setLoading(false));
  }, []);

  const filtered = proveedores?.filter(p =>
    p?.nombre?.toLowerCase()?.includes(search?.toLowerCase()) ||
    p?.nif_cif?.toLowerCase()?.includes(search?.toLowerCase())
  );

  const handleCreate = async () => {
    if (!newNombre?.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const { data, error: err } = await tiendaDb?.from('proveedores')?.insert({
        nombre: newNombre?.trim(),
        nif_cif: newCif?.trim() || null,
        activo: true,
      })?.select()?.single();
      if (err) throw new Error(err?.message);
      onSelect(data);
    } catch (e) {
      setError(e?.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.90)' }}>
      <div className="w-full max-w-md rounded-2xl flex flex-col max-h-[80vh]" style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.12)' }}>
        <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <h3 className="font-oranienbaum font-bold text-white text-base">Asignar Proveedor</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80"><Icon name="X" size={15} /></button>
        </div>

        {ocrNombre && (
          <div className="mx-5 mt-4 p-3 rounded-xl" style={{ background: `${AMBER}08`, border: `1px solid ${AMBER}20` }}>
            <p className="font-oranienbaum text-amber-300 text-xs font-semibold mb-0.5">Detectado por OCR</p>
            <p className="font-oranienbaum text-white/70 text-xs">{ocrNombre}{ocrCif ? ` · ${ocrCif}` : ''}</p>
          </div>
        )}

        <div className="px-5 pt-4 flex-shrink-0">
          <input value={search} onChange={e => setSearch(e?.target?.value)}
            placeholder="Buscar proveedor..."
            className={inputCls} style={{ ...inputSt }} />
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-1">
          {loading ? (
            <p className="font-oranienbaum text-white/40 text-sm text-center py-4">Cargando...</p>
          ) : filtered?.map(p => (
            <button key={p?.id} onClick={() => onSelect(p)}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-left transition-colors hover:bg-white/[0.06]"
              style={{ background: p?.id === currentProveedorId ? `${ACCENT}10` : 'transparent', border: `1px solid ${p?.id === currentProveedorId ? ACCENT + '30' : 'transparent'}` }}>
              <div>
                <p className="font-oranienbaum text-white/85 text-sm">{p?.nombre}</p>
                {p?.nif_cif && <p className="font-oranienbaum text-white/40 text-xs">{p?.nif_cif}</p>}
              </div>
              {p?.id === currentProveedorId && <Icon name="Check" size={14} style={{ color: ACCENT }} />}
            </button>
          ))}
          {!loading && filtered?.length === 0 && (
            <p className="font-oranienbaum text-white/35 text-sm text-center py-4">Sin resultados</p>
          )}
        </div>

        <div className="px-5 pb-5 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          {!showCreate ? (
            <button onClick={() => setShowCreate(true)}
              className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-oranienbaum"
              style={{ background: `${ACCENT}10`, color: ACCENT, border: `1px solid ${ACCENT}25` }}>
              <Icon name="Plus" size={14} />Crear nuevo proveedor
            </button>
          ) : (
            <div className="mt-3 space-y-2">
              <input value={newNombre} onChange={e => setNewNombre(e?.target?.value)}
                placeholder="Nombre del proveedor *"
                className={inputCls} style={inputSt} />
              <input value={newCif} onChange={e => setNewCif(e?.target?.value)}
                placeholder="CIF/NIF (opcional)"
                className={inputCls} style={inputSt} />
              {error && <p className="font-oranienbaum text-red-400 text-xs">{error}</p>}
              <div className="flex gap-2">
                <button onClick={() => setShowCreate(false)}
                  className="flex-1 px-3 py-2 rounded-xl text-xs font-oranienbaum"
                  style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.50)', border: '1px solid rgba(255,255,255,0.10)' }}>
                  Cancelar
                </button>
                <button onClick={handleCreate} disabled={saving || !newNombre?.trim()}
                  className="flex-1 px-3 py-2 rounded-xl text-xs font-oranienbaum font-semibold disabled:opacity-50"
                  style={{ background: `${ACCENT}20`, color: ACCENT, border: `1px solid ${ACCENT}40` }}>
                  {saving ? 'Creando...' : 'Crear'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── MAIN REVISION MODAL ───────────────────────────────────────────────────────
const FacturaRevisionModal = ({ ingesta, onClose, onRefresh }) => {
  const [factura, setFactura] = useState(null);
  const [lineas, setLineas] = useState([]);
  const [proveedor, setProveedor] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showProveedorSelector, setShowProveedorSelector] = useState(false);
  const [confirm, setConfirm] = useState(null); // { type, ... }
  const [activeTab, setActiveTab] = useState('cabecera'); // cabecera | lineas | documento

  // Load factura + lineas
  const loadData = useCallback(async () => {
    if (!ingesta?.factura_id) {
      setLoadingData(false);
      return;
    }
    try {
      const [facturaResult, lineasResult] = await Promise.all([
        tiendaDb?.from('facturas_compra')?.select('*, proveedor:proveedor_id(id, nombre, nif_cif, email, telefono, forma_pago)')?.eq('id', ingesta?.factura_id)?.single(),
        tiendaDb?.from('lineas_factura_compra')?.select('*')?.eq('factura_id', ingesta?.factura_id)?.order('created_at'),
      ]);
      const fc = facturaResult?.data;
      const lns = lineasResult?.data;
      setFactura(fc || null);
      setProveedor(fc?.proveedor || null);
      setLineas(lns || []);
    } catch (e) {
      setError(e?.message);
    } finally {
      setLoadingData(false);
    }
  }, [ingesta?.factura_id]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load PDF URL
  useEffect(() => {
    const path = ingesta?.storage_path || factura?.storage_path;
    const bucket = ingesta?.storage_bucket || factura?.storage_bucket || 'facturas-compras';
    if (path) {
      getAttachmentSignedUrl(path, bucket)?.then(url => setPdfUrl(url))?.catch(() => setPdfUrl(null));
    }
  }, [ingesta?.storage_path, factura?.storage_path, ingesta?.storage_bucket, factura?.storage_bucket]);

  // ── HEADER EDIT ──
  const updateFacturaField = (field, value) => {
    setFactura(prev => ({ ...prev, [field]: value }));
  };

  const saveHeader = async () => {
    if (!factura?.id) return;
    setSaving(true);
    setError(null);
    try {
      const patch = {
        proveedor_id: proveedor?.id || null,
        numero_factura: factura?.numero_factura,
        fecha_factura: factura?.fecha_factura || null,
        fecha_vencimiento: factura?.fecha_vencimiento || null,
        base_imponible: parseFloat(factura?.base_imponible) || 0,
        iva: parseFloat(factura?.iva) || 0,
        total: parseFloat(factura?.total) || 0,
        forma_pago: factura?.forma_pago || null,
        observaciones: factura?.observaciones || null,
        cif_proveedor: factura?.cif_proveedor || null,
      };
      const { error: err } = await tiendaDb?.from('facturas_compra')?.update(patch)?.eq('id', factura?.id);
      if (err) throw new Error(err?.message);
      setSuccess('Cabecera guardada');
      setTimeout(() => setSuccess(null), 2500);
    } catch (e) {
      setError(e?.message);
    } finally {
      setSaving(false);
    }
  };

  // ── LINES EDIT ──
  const updateLinea = (idx, field, value) => {
    setLineas(prev => prev?.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };

  const deleteLinea = async (idx) => {
    const linea = lineas?.[idx];
    if (linea?.id) {
      const { error: err } = await tiendaDb?.from('lineas_factura_compra')?.delete()?.eq('id', linea?.id);
      if (err) { setError(err?.message); return; }
    }
    setLineas(prev => prev?.filter((_, i) => i !== idx));
  };

  const addLinea = () => {
    setLineas(prev => [...prev, {
      _new: true,
      factura_id: factura?.id,
      descripcion: '',
      cantidad: 1,
      precio_unitario: 0,
      porcentaje_iva: 10,
      descuento: 0,
      subtotal: 0,
      unidad: 'ud',
      numero_lote: '',
      fecha_caducidad: null,
      referencia_proveedor: '',
    }]);
  };

  const saveLineas = async () => {
    if (!factura?.id) return;
    setSaving(true);
    setError(null);
    try {
      for (const linea of lineas) {
        const payload = {
          factura_id: factura?.id,
          descripcion: linea?.descripcion || '',
          cantidad: parseFloat(linea?.cantidad) || 0,
          precio_unitario: parseFloat(linea?.precio_unitario) || 0,
          porcentaje_iva: parseFloat(linea?.porcentaje_iva) || 10,
          descuento: parseFloat(linea?.descuento) || 0,
          subtotal: parseFloat(linea?.subtotal) || (parseFloat(linea?.cantidad) || 0) * (parseFloat(linea?.precio_unitario) || 0),
          unidad: linea?.unidad || 'ud',
          numero_lote: linea?.numero_lote || null,
          fecha_caducidad: linea?.fecha_caducidad || null,
          referencia_proveedor: linea?.referencia_proveedor || null,
        };
        if (linea?._new) {
          await tiendaDb?.from('lineas_factura_compra')?.insert(payload);
        } else if (linea?.id) {
          await tiendaDb?.from('lineas_factura_compra')?.update(payload)?.eq('id', linea?.id);
        }
      }
      await loadData();
      setSuccess('Líneas guardadas');
      setTimeout(() => setSuccess(null), 2500);
    } catch (e) {
      setError(e?.message);
    } finally {
      setSaving(false);
    }
  };

  // ── APPROVE ──
  const handleAprobar = async () => {
    setSaving(true);
    setError(null);
    try {
      await aprobarFactura(ingesta?.id, factura?.id);
      setSuccess('Factura aprobada → LISTA_PARA_REVISAR');
      setTimeout(() => { onRefresh?.(); onClose(); }, 1500);
    } catch (e) {
      setError(e?.message);
    } finally {
      setSaving(false);
    }
  };

  // ── DELETE ──
  const handleEliminar = async () => {
    setSaving(true);
    setError(null);
    try {
      await eliminarFactura(ingesta?.id, factura?.id);
      onRefresh?.();
      onClose();
    } catch (e) {
      setError(e?.message);
      setSaving(false);
    }
  };

  // ── MARK NOT INVOICE ──
  const handleNoFactura = async () => {
    setSaving(true);
    setError(null);
    try {
      await marcarNoFactura(ingesta?.id, factura?.id);
      onRefresh?.();
      onClose();
    } catch (e) {
      setError(e?.message);
      setSaving(false);
    }
  };

  // ── REPROCESS ──
  const handleReprocesar = async () => {
    setSaving(true);
    setError(null);
    try {
      await reprocesarIngesta(ingesta?.id);
      onRefresh?.();
      onClose();
    } catch (e) {
      setError(e?.message);
      setSaving(false);
    }
  };

  const ocrData = ingesta?.ocr_metadata;
  const ocrProveedor = ocrData?.proveedor || {};
  const canApprove = proveedor?.id && factura?.numero_factura && factura?.fecha_factura && parseFloat(factura?.total) > 0 && lineas?.length > 0;
  const canReprocess = ingesta?.estado === 'ERROR' || ingesta?.estado === 'REQUIERE_REVISION';

  const tabs = [
    { id: 'cabecera', label: 'Cabecera', icon: 'FileText' },
    { id: 'lineas', label: `Líneas (${lineas?.length})`, icon: 'List' },
    { id: 'documento', label: 'Documento', icon: 'File' },
  ];

  if (!ingesta) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.88)' }}>
        <div className="w-full max-w-4xl my-6 rounded-2xl flex flex-col" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>

          {/* ── MODAL HEADER ── */}
          <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${ORANGE}15`, border: `1px solid ${ORANGE}30` }}>
                <Icon name="ClipboardEdit" size={16} style={{ color: ORANGE }} />
              </div>
              <div>
                <h2 className="font-oranienbaum font-bold text-white text-lg">Revisar Factura</h2>
                <p className="font-oranienbaum text-white/40 text-xs">
                  {ingesta?.subject || ingesta?.filename || '—'}
                  {ingesta?.motivo_revision && <span className="ml-2 text-amber-400">· {ingesta?.motivo_revision}</span>}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80"><Icon name="X" size={16} /></button>
          </div>

          {/* ── TABS ── */}
          <div className="flex items-center gap-1 px-6 pt-4 flex-shrink-0">
            {tabs?.map(t => (
              <button key={t?.id} onClick={() => setActiveTab(t?.id)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-oranienbaum transition-all"
                style={activeTab === t?.id
                  ? { background: `${CYAN}20`, color: CYAN, border: `1px solid ${CYAN}40` }
                  : { color: 'rgba(255,255,255,0.40)', border: '1px solid transparent' }}>
                <Icon name={t?.icon} size={12} />{t?.label}
              </button>
            ))}
          </div>

          {/* ── CONTENT ── */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loadingData ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-[#22D3EE] animate-spin" />
              </div>
            ) : (
              <>
                {/* ── CABECERA TAB ── */}
                {activeTab === 'cabecera' && (
                  <div className="space-y-5">
                    {/* Proveedor */}
                    <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                      <div className="flex items-center justify-between mb-3">
                        <p className="font-oranienbaum text-white/50 text-xs uppercase tracking-wider">Proveedor</p>
                        <button onClick={() => setShowProveedorSelector(true)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-oranienbaum"
                          style={{ background: `${BLUE}15`, color: BLUE, border: `1px solid ${BLUE}30` }}>
                          <Icon name="UserSearch" size={11} />{proveedor ? 'Cambiar' : 'Asignar'}
                        </button>
                      </div>
                      {proveedor ? (
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${ACCENT}15` }}>
                            <Icon name="Building2" size={14} style={{ color: ACCENT }} />
                          </div>
                          <div>
                            <p className="font-oranienbaum text-white/85 text-sm font-semibold">{proveedor?.nombre}</p>
                            {proveedor?.nif_cif && <p className="font-oranienbaum text-white/45 text-xs">{proveedor?.nif_cif}</p>}
                          </div>
                          <button onClick={() => setProveedor(null)} className="ml-auto p-1 rounded text-white/30 hover:text-red-400">
                            <Icon name="X" size={12} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${AMBER}10` }}>
                            <Icon name="AlertTriangle" size={14} style={{ color: AMBER }} />
                          </div>
                          <div>
                            <p className="font-oranienbaum text-amber-400 text-sm">Sin proveedor asignado</p>
                            {ocrProveedor?.nombre && (
                              <p className="font-oranienbaum text-white/40 text-xs">OCR detectó: {ocrProveedor?.nombre}{ocrProveedor?.nif_cif ? ` (${ocrProveedor?.nif_cif})` : ''}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Datos factura */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block font-oranienbaum text-white/45 text-xs mb-1">Nº Factura *</label>
                        <input value={factura?.numero_factura || ''} onChange={e => updateFacturaField('numero_factura', e?.target?.value)}
                          className={inputCls} style={inputSt} placeholder="A/1093" />
                      </div>
                      <div>
                        <label className="block font-oranienbaum text-white/45 text-xs mb-1">CIF/NIF Proveedor</label>
                        <input value={factura?.cif_proveedor || ''} onChange={e => updateFacturaField('cif_proveedor', e?.target?.value)}
                          className={inputCls} style={inputSt} placeholder="B12345678" />
                      </div>
                      <div>
                        <label className="block font-oranienbaum text-white/45 text-xs mb-1">Fecha Factura *</label>
                        <input type="date" value={factura?.fecha_factura || ''} onChange={e => updateFacturaField('fecha_factura', e?.target?.value)}
                          className={inputCls} style={{ ...inputSt, colorScheme: 'dark' }} />
                      </div>
                      <div>
                        <label className="block font-oranienbaum text-white/45 text-xs mb-1">Fecha Vencimiento</label>
                        <input type="date" value={factura?.fecha_vencimiento || ''} onChange={e => updateFacturaField('fecha_vencimiento', e?.target?.value)}
                          className={inputCls} style={{ ...inputSt, colorScheme: 'dark' }} />
                      </div>
                      <div>
                        <label className="block font-oranienbaum text-white/45 text-xs mb-1">Base Imponible</label>
                        <input type="number" step="0.01" value={factura?.base_imponible ?? ''} onChange={e => updateFacturaField('base_imponible', e?.target?.value)}
                          className={inputCls} style={inputSt} placeholder="0.00" />
                      </div>
                      <div>
                        <label className="block font-oranienbaum text-white/45 text-xs mb-1">IVA</label>
                        <input type="number" step="0.01" value={factura?.iva ?? ''} onChange={e => updateFacturaField('iva', e?.target?.value)}
                          className={inputCls} style={inputSt} placeholder="0.00" />
                      </div>
                      <div>
                        <label className="block font-oranienbaum text-white/45 text-xs mb-1">Total *</label>
                        <input type="number" step="0.01" value={factura?.total ?? ''} onChange={e => updateFacturaField('total', e?.target?.value)}
                          className={inputCls} style={inputSt} placeholder="0.00" />
                      </div>
                      <div>
                        <label className="block font-oranienbaum text-white/45 text-xs mb-1">Forma de Pago</label>
                        <input value={factura?.forma_pago || ''} onChange={e => updateFacturaField('forma_pago', e?.target?.value)}
                          className={inputCls} style={inputSt} placeholder="Transferencia, Contado..." />
                      </div>
                    </div>

                    {/* OCR info */}
                    {ingesta?.ocr_confianza != null && (
                      <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                        <Icon name="Cpu" size={14} style={{ color: ingesta?.ocr_confianza >= 80 ? ACCENT : ingesta?.ocr_confianza >= 60 ? AMBER : RED }} />
                        <span className="font-oranienbaum text-white/50 text-xs">Confianza OCR: </span>
                        <span className="font-oranienbaum text-sm font-semibold" style={{ color: ingesta?.ocr_confianza >= 80 ? ACCENT : ingesta?.ocr_confianza >= 60 ? AMBER : RED }}>
                          {Math.round(ingesta?.ocr_confianza)}%
                        </span>
                        <span className="font-oranienbaum text-white/35 text-xs ml-auto">Estado: {ingesta?.estado}</span>
                      </div>
                    )}

                    <div className="flex justify-end">
                      <button onClick={saveHeader} disabled={saving}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-oranienbaum font-semibold disabled:opacity-50"
                        style={{ background: `${BLUE}20`, color: BLUE, border: `1px solid ${BLUE}40` }}>
                        {saving ? <><div className="w-3.5 h-3.5 rounded-full border border-white/20 border-t-blue-400 animate-spin" />Guardando...</> : <><Icon name="Save" size={14} />Guardar cabecera</>}
                      </button>
                    </div>
                  </div>
                )}

                {/* ── LÍNEAS TAB ── */}
                {activeTab === 'lineas' && (
                  <div className="space-y-4">
                    <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
                      <table className="w-full min-w-max">
                        <thead>
                          <tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            {['Ref.', 'Descripción', 'Cant.', 'Unidad', 'P.Unit.', 'Dto%', 'IVA%', 'Subtotal', 'Lote', 'Caducidad', '']?.map(h => (
                              <th key={h} className="px-2 py-2.5 text-left whitespace-nowrap">
                                <span className="font-oranienbaum text-white/35 text-[10px] uppercase tracking-wider">{h}</span>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {lineas?.length === 0 ? (
                            <tr><td colSpan={11} className="px-4 py-8 text-center">
                              <p className="font-oranienbaum text-white/35 text-sm">Sin líneas. Añade una manualmente.</p>
                            </td></tr>
                          ) : lineas?.map((linea, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                              <td className="px-2 py-2">
                                <input value={linea?.referencia_proveedor || ''} onChange={e => updateLinea(idx, 'referencia_proveedor', e?.target?.value)}
                                  className="w-20 px-2 py-1 rounded-lg text-xs font-oranienbaum text-white/80 outline-none"
                                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                              </td>
                              <td className="px-2 py-2">
                                <input value={linea?.descripcion || ''} onChange={e => updateLinea(idx, 'descripcion', e?.target?.value)}
                                  className="w-40 px-2 py-1 rounded-lg text-xs font-oranienbaum text-white/80 outline-none"
                                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                              </td>
                              <td className="px-2 py-2">
                                <input type="number" value={linea?.cantidad ?? ''} onChange={e => updateLinea(idx, 'cantidad', e?.target?.value)}
                                  className="w-16 px-2 py-1 rounded-lg text-xs font-oranienbaum text-white/80 outline-none text-right"
                                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                              </td>
                              <td className="px-2 py-2">
                                <input value={linea?.unidad || ''} onChange={e => updateLinea(idx, 'unidad', e?.target?.value)}
                                  className="w-14 px-2 py-1 rounded-lg text-xs font-oranienbaum text-white/80 outline-none"
                                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                              </td>
                              <td className="px-2 py-2">
                                <input type="number" step="0.01" value={linea?.precio_unitario ?? ''} onChange={e => updateLinea(idx, 'precio_unitario', e?.target?.value)}
                                  className="w-20 px-2 py-1 rounded-lg text-xs font-oranienbaum text-white/80 outline-none text-right"
                                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                              </td>
                              <td className="px-2 py-2">
                                <input type="number" step="0.01" value={linea?.descuento ?? ''} onChange={e => updateLinea(idx, 'descuento', e?.target?.value)}
                                  className="w-14 px-2 py-1 rounded-lg text-xs font-oranienbaum text-white/80 outline-none text-right"
                                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                              </td>
                              <td className="px-2 py-2">
                                <input type="number" step="0.01" value={linea?.porcentaje_iva ?? ''} onChange={e => updateLinea(idx, 'porcentaje_iva', e?.target?.value)}
                                  className="w-14 px-2 py-1 rounded-lg text-xs font-oranienbaum text-white/80 outline-none text-right"
                                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                              </td>
                              <td className="px-2 py-2">
                                <input type="number" step="0.01" value={linea?.subtotal ?? ''} onChange={e => updateLinea(idx, 'subtotal', e?.target?.value)}
                                  className="w-20 px-2 py-1 rounded-lg text-xs font-oranienbaum text-white/80 outline-none text-right"
                                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                              </td>
                              <td className="px-2 py-2">
                                <input value={linea?.numero_lote || ''} onChange={e => updateLinea(idx, 'numero_lote', e?.target?.value)}
                                  className="w-20 px-2 py-1 rounded-lg text-xs font-oranienbaum text-white/80 outline-none"
                                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                              </td>
                              <td className="px-2 py-2">
                                <input type="date" value={linea?.fecha_caducidad || ''} onChange={e => updateLinea(idx, 'fecha_caducidad', e?.target?.value)}
                                  className="w-28 px-2 py-1 rounded-lg text-xs font-oranienbaum text-white/80 outline-none"
                                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', colorScheme: 'dark' }} />
                              </td>
                              <td className="px-2 py-2">
                                <button onClick={() => deleteLinea(idx)} className="p-1 rounded text-white/30 hover:text-red-400 transition-colors">
                                  <Icon name="Trash2" size={13} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center justify-between">
                      <button onClick={addLinea}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-oranienbaum"
                        style={{ background: `${ACCENT}10`, color: ACCENT, border: `1px solid ${ACCENT}25` }}>
                        <Icon name="Plus" size={12} />Añadir línea
                      </button>
                      <button onClick={saveLineas} disabled={saving}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-oranienbaum font-semibold disabled:opacity-50"
                        style={{ background: `${BLUE}20`, color: BLUE, border: `1px solid ${BLUE}40` }}>
                        {saving ? 'Guardando...' : <><Icon name="Save" size={14} />Guardar líneas</>}
                      </button>
                    </div>
                  </div>
                )}

                {/* ── DOCUMENTO TAB ── */}
                {activeTab === 'documento' && (
                  <div className="space-y-4">
                    {pdfUrl ? (
                      <>
                        <div className="flex items-center gap-2 flex-wrap">
                          <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-oranienbaum"
                            style={{ background: `${BLUE}15`, color: BLUE, border: `1px solid ${BLUE}30` }}>
                            <Icon name="ExternalLink" size={14} />Ver documento original
                          </a>
                          <a href={pdfUrl} download
                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-oranienbaum"
                            style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.70)', border: '1px solid rgba(255,255,255,0.12)' }}>
                            <Icon name="Download" size={14} />Descargar
                          </a>
                        </div>
                        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)', height: '500px' }}>
                          <iframe src={pdfUrl} className="w-full h-full" title="Documento factura" />
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-16 gap-4">
                        <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                          <Icon name="FileX" size={28} style={{ color: 'rgba(255,255,255,0.25)' }} />
                        </div>
                        <p className="font-oranienbaum text-white/40 text-sm">Documento no disponible</p>
                        <p className="font-oranienbaum text-white/25 text-xs">{ingesta?.storage_path || 'Sin ruta de almacenamiento'}</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── FEEDBACK ── */}
          {(error || success) && (
            <div className="px-6 py-2 flex-shrink-0">
              {error && (
                <div className="p-3 rounded-xl" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
                  <p className="font-oranienbaum text-red-400 text-xs">{error}</p>
                </div>
              )}
              {success && (
                <div className="p-3 rounded-xl" style={{ background: `${ACCENT}08`, border: `1px solid ${ACCENT}20` }}>
                  <p className="font-oranienbaum text-xs" style={{ color: ACCENT }}>✓ {success}</p>
                </div>
              )}
            </div>
          )}

          {/* ── ACTIONS FOOTER ── */}
          <div className="flex items-center gap-2 px-6 py-4 flex-shrink-0 flex-wrap" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            {/* Approve */}
            <button onClick={() => canApprove && handleAprobar()} disabled={saving || !canApprove}
              title={!canApprove ? 'Requiere: proveedor, nº factura, fecha, total > 0 y al menos 1 línea' : ''}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-oranienbaum font-semibold disabled:opacity-40"
              style={{ background: canApprove ? `${ACCENT}20` : 'rgba(255,255,255,0.05)', color: canApprove ? ACCENT : 'rgba(255,255,255,0.35)', border: `1px solid ${canApprove ? ACCENT + '40' : 'rgba(255,255,255,0.10)'}` }}>
              <Icon name="CheckCircle" size={15} />Aprobar factura
            </button>

            {/* Reprocess */}
            {canReprocess && (
              <button onClick={() => setConfirm({ type: 'reprocesar' })} disabled={saving}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-oranienbaum disabled:opacity-50"
                style={{ background: `${ORANGE}15`, color: ORANGE, border: `1px solid ${ORANGE}30` }}>
                <Icon name="RefreshCw" size={14} />Reprocesar OCR
              </button>
            )}

            {/* Mark not invoice */}
            <button onClick={() => setConfirm({ type: 'noFactura' })} disabled={saving}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-oranienbaum disabled:opacity-50"
              style={{ background: `${PURPLE}15`, color: PURPLE, border: `1px solid ${PURPLE}30` }}>
              <Icon name="Ban" size={14} />No es factura
            </button>

            {/* Delete */}
            <button onClick={() => setConfirm({ type: 'eliminar' })} disabled={saving}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-oranienbaum disabled:opacity-50"
              style={{ background: `${RED}12`, color: RED, border: `1px solid ${RED}25` }}>
              <Icon name="Trash2" size={14} />Eliminar
            </button>

            <button onClick={onClose}
              className="ml-auto px-4 py-2.5 rounded-xl text-sm font-oranienbaum"
              style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.10)' }}>
              Cerrar
            </button>
          </div>
        </div>
      </div>

      {/* ── PROVEEDOR SELECTOR ── */}
      {showProveedorSelector && (
        <ProveedorSelector
          currentProveedorId={proveedor?.id}
          currentProveedorNombre={proveedor?.nombre}
          ocrNombre={ocrProveedor?.nombre}
          ocrCif={ocrProveedor?.nif_cif}
          onSelect={(p) => { setProveedor(p); setShowProveedorSelector(false); }}
          onClose={() => setShowProveedorSelector(false)}
        />
      )}

      {/* ── CONFIRM DIALOGS ── */}
      {confirm?.type === 'eliminar' && (
        <ConfirmDialog
          title="Eliminar factura"
          message="¿Seguro que quieres eliminar esta factura? Esta acción no se puede deshacer."
          details={[
            `Proveedor: ${proveedor?.nombre || ingesta?.proveedor_detectado || '—'}`,
            `Nº Factura: ${factura?.numero_factura || '—'}`,
            `Total: ${fmtEur(factura?.total)}`,
          ]}
          confirmLabel="Eliminar"
          confirmColor={RED}
          onConfirm={() => { setConfirm(null); handleEliminar(); }}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm?.type === 'noFactura' && (
        <ConfirmDialog
          title="Marcar como no factura"
          message="Este documento se marcará como IGNORADO. La factura ERP asociada será eliminada si existe. El documento original se conserva."
          details={[]}
          confirmLabel="Confirmar"
          confirmColor={PURPLE}
          onConfirm={() => { setConfirm(null); handleNoFactura(); }}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm?.type === 'reprocesar' && (
        <ConfirmDialog
          title="Reprocesar OCR"
          message="Se volverá a procesar el PDF original guardado en Storage. No se creará un nuevo registro de email."
          details={[]}
          confirmLabel="Reprocesar"
          confirmColor={ORANGE}
          onConfirm={() => { setConfirm(null); handleReprocesar(); }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
};

export default FacturaRevisionModal;
